import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../supabase';

// Define types for better TypeScript support
const AuthContext = createContext({
  user: null,
  userProfile: null,
  loading: true,
  isAuthenticated: false,
  isAdmin: false,
  signUp: async (email, password, userData) => ({ data: null, error: null }),
  signIn: async (email, password) => ({ data: null, error: null }),
  signOut: async () => ({ error: null })
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Load user profile and check admin status
  const loadUserProfile = async (userId) => {
    if (!userId) return null;
    
    try {
      const { database } = await import('../supabase');
      const { data, error } = await database.getUserProfile(userId);

      if (data && !error) {
        setUserProfile(data);
        return data;
      } else {
        // Create default profile if it doesn't exist
        const userMetadata = user?.user_metadata || {};
        const defaultProfile = {
          first_name: userMetadata.firstName || userMetadata.first_name || '',
          last_name: userMetadata.lastName || userMetadata.last_name || '',
          role: 'customer'
        };

        const { data: newProfile, error: createError } = await database.createUserProfile(userId, defaultProfile);

        if (newProfile && !createError) {
          setUserProfile(newProfile);
          return newProfile;
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }

    return null;
  };

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const getInitialSession = async () => {
      try {
        const { user: initialUser, error } = await auth.getUser();
        
        // Safety timeout - force loading to false after 10 seconds
        timeoutId = setTimeout(() => {
          console.warn('AuthContext: Safety timeout reached, forcing loading=false');
          if (isMounted) {
            setLoading(false);
          }
        }, 10000);
        
        if (initialUser && !error && isMounted) {
          setUser(initialUser);
          // Load user profile asynchronously
          loadUserProfile(initialUser.id).catch(err => {
            console.warn('Background profile loading failed:', err);
          });
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = auth.onAuthStateChange(async (event, session) => {
      if (session?.user && isMounted) {
        setUser(session.user);
        // Load user profile asynchronously (don't await)
        loadUserProfile(session.user.id).catch(err => {
          console.warn('Background profile loading failed:', err);
        });
      } else if (isMounted) {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password, userData = {}) => {
    setLoading(true);
    try {
      const { data, error } = await auth.signUp(email, password, {
        options: {
          data: userData
        }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await auth.signIn(email, password);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
